import { useCallback, useEffect, useState } from 'react';

interface IUseStateObjectConfig {
  /** If true, the state object will be reinitialized when `initialValues` changes. */
  enableReinitialize?: boolean;
}

interface IUseStateObjectResult<T> {
  /** Resets `values` back to `initialValues`. */
  resetValues: () => void;
  /** Updates a specific value. */
  setValue: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Updates multiple values at a time. */
  setValues: (properties: Partial<T>) => void;
  /** The state object. */
  values: T;
}

/**
 * Stores a state object and provides utility functions to update it.
 *
 * This hook was inspired by our use of Formik and the `setFieldValue` function.
 * We often don't need the full functionality of Formik, we just need to store a collection of values and update them as needed.
 */
export const useStateObject = <T extends Record<string, any>>(
  initialValues: T,
  config?: IUseStateObjectConfig
): IUseStateObjectResult<T> => {
  const [_initialValues, _setInitialValues] = useState(initialValues);
  const [_values, _setValues] = useState<T>(initialValues);

  useEffect(() => {
    if (config?.enableReinitialize && initialValues !== _values) {
      _setInitialValues(initialValues);
      _setValues(initialValues);
    }
  }, [initialValues]);

  const resetValues = useCallback(() => {
    _setValues(_initialValues);
  }, [_initialValues]);

  const setValues = (properties: Partial<T>) => {
    _setValues((prevState) => ({
      ...prevState,
      ...properties,
    }));
  };

  const setValue = <K extends keyof T>(key: K, value: T[K]) => {
    _setValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  return { resetValues, setValue, setValues, values: _values };
};
